import { ActionIcon, Menu, Stack, Text } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import {
  IconCheck,
  IconCircleCheck,
  IconCircleMinus,
  IconCurrencyDollarOff,
  IconDotsVertical,
  IconLink,
  IconRepeatOnce,
} from '@tabler/icons-react';
import { Tariff } from 'interfaces';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';

interface ClaimTariffActionsMenuProps {
  claimID: string;
  tariff: Tariff;
  canModify: boolean;
  canReconcileTariff: boolean;
  canResubmitTariff: boolean;
  canWriteOffTariff: boolean;
  canMatchTariff?: boolean;
  onTariffResubmit?: (payload: { claimID: string; tariffID: string }) => void;
  onTariffReconcile?: (payload: { claimID: string; tariffID: string }) => void;
  onTariffWriteOff?: (payload: { claimID: string; tariffID: string }) => void;
  onTariffMatch?: (payload: { claimID: string; tariffID: string }) => void;
  onTariffMarkAs?: (payload: {
    claimID: string;
    tariffID: string;
    status: string;
  }) => void;
}

export const ClaimTariffActionsMenu = ({
  claimID,
  tariff,
  canModify,
  canReconcileTariff,
  canResubmitTariff,
  canWriteOffTariff,
  canMatchTariff = false,
  onTariffResubmit,
  onTariffReconcile,
  onTariffWriteOff,
  onTariffMatch,
  onTariffMarkAs,
}: ClaimTariffActionsMenuProps) => {
  const { translate } = useTranslation();
  const canModifyTariff =
    canModify && tariff.status !== CLAIM_TARIFF_STATUS.PENDING;
  const tariffLabel = `${tariff.prefix ?? ''}${tariff.code ?? ''}`.trim();
  const rowActionsForLabel = tariffLabel
    ? translate('pages:claims.actions.menu.rowActionsForTariff', {
        tariffLabel,
      })
    : translate('pages:claims.actions.menu.rowActionsForTariffWithoutLabel');

  return (
    <Menu shadow="md" width={260} position="right-start" withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          title={rowActionsForLabel}
          aria-label={rowActionsForLabel}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <IconDotsVertical size={18} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {tariff.status === CLAIM_TARIFF_STATUS.PENDING && (
          <Menu.Label>
            {translate('pages:claims.pendingActionDisabled')}
          </Menu.Label>
        )}
        <Menu.Label>
          <Stack gap={2}>
            <Text size="xs" fw={700} tt="uppercase" lh={1.1}>
              {tariffLabel || 'Tariff row'}
            </Text>
            <Text size="xs" c="dimmed">
              {translate(
                'pages:claims.actions.menu.appliesToThisRowOnly',
                'Applies to this row only'
              )}
            </Text>
          </Stack>
        </Menu.Label>
        <Menu.Item
          leftSection={<IconCircleCheck size={16} />}
          disabled={!canModifyTariff}
          onClick={() =>
            onTariffMarkAs?.({
              claimID,
              tariffID: tariff.id,
              status: CLAIM_TARIFF_STATUS.READY,
            })
          }
        >
          {translate(
            'pages:claims.actions.menu.markTariffReady',
            'Mark as ready'
          )}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconCircleMinus size={16} />}
          disabled={!canModifyTariff}
          onClick={() =>
            onTariffMarkAs?.({
              claimID,
              tariffID: tariff.id,
              status: CLAIM_TARIFF_STATUS.INCOMPLETE,
            })
          }
        >
          {translate(
            'pages:claims.actions.menu.markTariffIncomplete',
            'Mark as incomplete'
          )}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconRepeatOnce size={16} />}
          disabled={!canModifyTariff || !canResubmitTariff}
          onClick={() =>
            onTariffResubmit?.({
              claimID,
              tariffID: tariff.id,
            })
          }
        >
          {translate(
            'pages:claims.actions.menu.resubmitTariff',
            'Resubmit tariff'
          )}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconCheck size={16} />}
          disabled={!canModifyTariff || !canReconcileTariff}
          onClick={() =>
            onTariffReconcile?.({
              claimID,
              tariffID: tariff.id,
            })
          }
        >
          {translate(
            'pages:claims.actions.menu.reconcileTariff',
            'Reconcile tariff'
          )}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconLink size={16} />}
          disabled={!canModifyTariff || !canMatchTariff}
          onClick={() =>
            onTariffMatch?.({
              claimID,
              tariffID: tariff.id,
            })
          }
        >
          {translate('pages:claims.actions.menu.matchTariff', 'Match tariff')}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconCurrencyDollarOff size={16} />}
          disabled={!canModifyTariff || !canWriteOffTariff}
          onClick={() =>
            onTariffWriteOff?.({
              claimID,
              tariffID: tariff.id,
            })
          }
        >
          {translate(
            'pages:claims.actions.menu.writeOffTariff',
            'Write off tariff'
          )}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
